<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useField } from 'vee-validate'
import { string } from 'yup'
import { isEmpty, find, delay, capitalize } from 'lodash-es'
import moment from 'moment'

import APICard from '@/components/api-plan/APICard.vue'
import Button from '@/components/atoms/button/index.vue'
import DoodleMessage from '@/components/utils/DoodleMessage.vue'

import {
  initBundle,
  initCompilerApi,
  cancelBundleSubscription,
  getApiPaymentMethod,
  getBundlePaymentMethod,
  subscribeToApi,
  subscribeToBundle,
  getPromotionCode,
  calcCouponDiscount
} from '@/services/subscription.service'
import { showUpdateCreditCard } from '@/services/payment.service'
import { getSubscriptionInfo } from '@/services/teach.service'
import authService from '@/services/auth.service'
import utilModelsService from '@/services/util.models.service'

import {
  apiPlansData,
  pluginPlansData,
  yearlyApiPlansData,
  type IApiPlan,
  type IBundledPlan
} from '@/utils/sharedData/plans'
import {
  BILLING_CYCLE,
  SUBSCRIPTION_PLAN_NAME,
  SUBSCRIPTION_TYPE
} from '@/components/shared/subscription/enum/subscription.enum'
import type { ISubscribeRequest } from '@/components/shared/subscription/interface/ISubscribeRequest'
import type { IPaymentMethod } from '@/components/shared/subscription/interface/IPaymentMethod'

import { AUTHMODELHS, IDEVIEWMODELS } from '@/utils/models'

import { useAuthStore } from '@/stores/auth.store'
import { useApiStore } from '@/stores/api.store'
import { useSubscribeStore } from '@/stores/subscribe.store'
import { useAssessmentStore } from '@/stores/assessment.store'
import { useOrganisationStore } from '@/stores/organisation.store'

import Visa from '@/assets/images/payment/Visa.png'
import Mastercard from '@/assets/images/payment/Mastercard.png'
import JCB from '@/assets/images/payment/JCB.png'
import DinersClub from '@/assets/images/payment/DinersClub.png'
import Discover from '@/assets/images/payment/Discover.png'
import UnionPay from '@/assets/images/payment/UnionPay.png'

type StateKey = 'isLoading' | 'isError' | 'isSuccess'

const props = defineProps({
  plan: {
    type: String,
    required: false
  },
  onBordRedirect: {
    type: String,
    required: false
  },
  apiCardTitle: {
    type: String,
    required: false,
    default: 'Your card details'
  },
  isModal: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const apiStore = useApiStore()
const organisationStore = useOrganisationStore()
const assessmentStore = useAssessmentStore()
const subscribeStore = useSubscribeStore()

const completed = ref<boolean>(false)
const tokenID = ref<number | null>(null)
const isSubscribed = ref<boolean>(false)
const fetchedPaymentInfo = ref<boolean>(false)
const paymentInfo = ref<IPaymentMethod | null>(null)
const loading = ref<boolean>(true)
const validationState = ref({
  isLoading: false,
  isError: false,
  isSuccess: false
})
const message = ref<string>('')

const stripCard = ref<any>(null)
const stripe = ref<any>(null)
const verifyingCoupon = ref<boolean>(false)
const validCoupon = ref<boolean>(false)
const invalidCoupon = ref<boolean>(false)
const discountDetails = ref<ReturnType<typeof calcCouponDiscount> | null>(null)

const {
  value: coupon,
  errorMessage: couponError,
  validate: couponValidate,
  setErrors: setCouponErrors
} = useField<string>('coupon', string().required('Please enter coupon code'), {
  validateOnValueUpdate: false
})

const isUserloggedIn = computed(() => {
  return authStore.isUserloggedIn
})

const subscriptionType = computed(() => {
  if (props.plan) {
    return SUBSCRIPTION_TYPE.BUNDLE
  }
  return route.params.type
})

const selectedPlanName = computed(() => capitalize(route.params.plan as string))

const selectedPlan = computed(() => {
  if (props.plan) {
    return pluginPlansData.find((plan: IBundledPlan) => plan.name == props.plan)
  } else if (route.params.type == SUBSCRIPTION_TYPE.BUNDLE) {
    return pluginPlansData.find(
      (plan: IBundledPlan) => plan.type?.toLowerCase() == route.params.plan
    )
  } else {
    if (subscribeStore.apiBillingCycle === BILLING_CYCLE.Monthly) {
      return apiPlansData.find((plan: IBundledPlan) => plan.name == route.params.plan)
    } else {
      return yearlyApiPlansData.find((plan: IBundledPlan) => plan.name == route.params.plan)
    }
  }
})

const plans = computed(() => {
  if (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE) {
    return pluginPlansData
  } else {
    return apiPlansData
  }
})

const hideCoupon = computed(() => {
  return selectedPlanName.value === SUBSCRIPTION_PLAN_NAME.FREE
})

const accountId = computed(() => {
  return organisationStore.activeAccountId
})

const formattedExpiresDate = computed(() => {
  if (!paymentInfo.value || !paymentInfo.value.expires) return ''

  const [month, year] = paymentInfo.value.expires.split('/')
  return `${month.trim().padStart(2, '0')} / ${year.trim()}`
})

const billingCycle = computed(() => {
  return subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE
    ? subscribeStore.billingCycle
    : subscribeStore.apiBillingCycle
})

const selectedPlanTitle = computed(() => {
  return subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE
    ? (selectedPlan.value as IBundledPlan)?.name + ' plan'
    : (selectedPlan.value as IApiPlan)?.credits + ' credits'
})

const doodleMessageProps = computed<{
  icon: 'error' | 'success' | 'loading' | undefined
  color: string | undefined
}>(() => {
  if (validationState.value.isError) {
    return { icon: 'error', color: 'text-red-600' }
  } else if (validationState.value.isSuccess) {
    return { icon: 'success', color: 'text-green-600' }
  } else if (validationState.value.isLoading) {
    return { icon: 'loading', color: 'text-gray-600' }
  }
  return { icon: undefined, color: undefined }
})

const tabProperties = reactive({
  isUserLoggedIn: isUserloggedIn,
  stripe: {},
  stripCard: {},
  clientPlan: '' as string,
  isUnlimited: false as any,
  clientFuturePlan: null,
  clientFuturePlanDetail: {},
  clientPlanDetail: {},
  isSubscriptionCancelled: false,
  hasPayAccount: false,
  stripeKey: ''
})

/**
 * Sets the state for a given key.
 * @param key - The key for which the state needs to be set.
 */
const setState = (key: StateKey) => {
  for (const stateKey in validationState.value) {
    if (stateKey === 'message') continue
    validationState.value[stateKey as StateKey] = stateKey === key
  }
}

/**
 * Handles the input change event
 * @param event - The input change event.x
 */
const handleInputChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  coupon.value = value
  validCoupon.value = false
  invalidCoupon.value = false
  setCouponErrors('')
  if (value === '' && message.value) {
    message.value = ''
  }
}

/**
 *  Get current subscribed plan info
 */
async function initPage() {
  try {
    let data = undefined

    // Init Bundle or Compiler
    if (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE) {
      data = await initBundle()
    } else {
      data = await initCompilerApi()
    }

    // Redirected from login page
    if (route.query.social || useSubscribeStore().redirectTo) {
      if (
        (subscriptionType.value == SUBSCRIPTION_TYPE.COMPILER_API && data.plan) ||
        (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE && data.plan) ||
        (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE &&
          selectedPlanName.value === SUBSCRIPTION_PLAN_NAME.FREE)
      ) {
        useSubscribeStore().setRedirectTo('')
        router.replace({ name: 'plans-payments' })
      }
    }

    if (data.plan) {
      tabProperties.clientPlan = data.plan
      tabProperties.isUnlimited = data.isUnlimited
      tabProperties.clientPlanDetail = find(plans.value, { name: data.plan }) || {}
      tabProperties.isSubscriptionCancelled = data.isCancelled
      tabProperties.hasPayAccount = data.hasStripeAccount

      if (data.isUnlimited || data.isCustom) {
        message.value = 'You are on a custom plan, please contact our support to change.'
      }
    } else {
      tabProperties.clientPlanDetail = {}
      tabProperties.clientPlan = ''
      tabProperties.isUnlimited = false
      tabProperties.isSubscriptionCancelled = false
      tabProperties.hasPayAccount = false
    }

    if (data.futurePlan) {
      tabProperties.clientFuturePlan = data.futurePlan
      tabProperties.clientFuturePlanDetail =
        find(plans.value, {
          name: data.futurePlan
        }) || {}
    } else {
      tabProperties.clientFuturePlan = null
      tabProperties.clientFuturePlanDetail = {}
    }
    tabProperties.stripeKey = data.stripeKey

    if (useSubscribeStore().redirectTo) {
      useSubscribeStore().setRedirectTo('')
    }
  } catch (e) {
    loading.value = false
  }
}

/**
 * Get payment information for subscription.
 * If exists, no need to fill stripe info again.
 */
async function getPaymentInfo() {
  try {
    let data = undefined
    let time = 0 /* Only delay loading for new users or expired card users */

    if (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE) {
      data = await getBundlePaymentMethod()
    } else {
      data = await getApiPaymentMethod()
    }

    if (data && !isEmpty(data)) {
      fetchedPaymentInfo.value = true
      paymentInfo.value = data

      const expires = moment(data.expires, 'MM/YYYY')

      if (expires.isBefore(new Date(), 'month')) {
        time = 500
        showUpdateCreditCard(
          tabProperties.stripeKey,
          stripCard,
          stripe,
          completed.value,
          message,
          setState
        )
      }
    } else {
      time = 500
      showUpdateCreditCard(
        tabProperties.stripeKey,
        stripCard,
        stripe,
        completed.value,
        message,
        setState
      )
    }
    setTimeout(() => {
      loading.value = false
    }, time)
  } catch (err: any) {
    loading.value = false
  }
}

/**
 * Clear card data
 */
function clearCardDetails() {
  stripCard.value.clear()
}

/**
 * Clear payment info
 */
function clearPaymentInfo() {
  paymentInfo.value = null
  showUpdateCreditCard(
    tabProperties.stripeKey,
    stripCard,
    stripe,
    completed.value,
    message,
    setState
  )
}

/**
 * Subscribe a plan
 * need to check whether have existed payment info
 */
function subscribePlan() {
  if (coupon.value && !(validCoupon.value || invalidCoupon.value)) {
    setState('isError')
    message.value = 'Please verify coupon code before applying'
    return
  }

  if (invalidCoupon.value) {
    return
  }

  message.value = ''
  setCouponErrors('')

  if (selectedPlan.value?.name !== SUBSCRIPTION_PLAN_NAME.FREE && !paymentInfo.value) {
    submitPaymentInfo()
  } else {
    proceedWithSubscription(null)
  }
}

/**
 *  Check the payment card info validate or not.
 *  If not, display the error message. If yes, eligible to subscribe.
 */
function submitPaymentInfo() {
  stripe.value.createToken(stripCard.value).then((res: any) => {
    if (res.error) {
      setState('isError')
      message.value = res.error.message
    } else {
      tokenID.value = res.token.id
      proceedWithSubscription(tokenID.value)
    }
  })
}

/**
 *  Subscribe a plan after the payment info has been checked.
 * @param token token id
 */
async function proceedWithSubscription(token: any) {
  setState('isLoading')
  message.value = 'We are processing your request. Please wait...'

  const reqObj: ISubscribeRequest = {
    plan:
      subscriptionType.value == SUBSCRIPTION_TYPE.COMPILER_API
        ? (selectedPlan.value! as IApiPlan).value
        : (selectedPlan.value as IBundledPlan).name!,
    token: token,
    accountId: accountId.value,
    billingCycle: billingCycle.value,
    promotionCode: coupon.value && validCoupon.value ? coupon.value : undefined
  }

  try {
    if (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE) {
      //Cancel paid -> Free
      if (
        selectedPlan.value?.value == SUBSCRIPTION_PLAN_NAME.PFREE ||
        selectedPlan.value?.name == SUBSCRIPTION_PLAN_NAME.PFREE
      ) {
        await cancelBundleSubscription({
          accountId: accountId.value
        })
      } else {
        //subscribe to paid
        await subscribeToBundle(reqObj, stripe.value)
          .then(() => {
            isSubscribed.value = true
          })
          .catch((error) => {
            apiStore.setGlobalMsg('')
            const errorMsg = error?.response?.data?.message as any

            if (errorMsg) {
              setState('isError')
              message.value = errorMsg
            }

            delay(() => {
              router.push('/pricing')
            }, 3000)
          })
      }
      delay(async () => {
        const res = await getSubscriptionInfo()
        assessmentStore.setSubscriptionInfo(res)
        organisationStore.setSubscriptionInfo(res)
      }, 500)

      // BE takes time to update limit soo calling after 3s
      delay(async () => {
        if (isSubscribed.value) {
          authService.initAuth()
        }
      }, 3000)
    } else {
      //Subscribe to Api
      await subscribeToApi(reqObj, stripe.value)
        .then(() => {
          isSubscribed.value = true
        })
        .catch((error) => {
          apiStore.setGlobalMsg('')
          const errorMsg = error?.response?.data?.message as any

          if (errorMsg) {
            setState('isError')
            message.value = errorMsg
          }

          delay(() => {
            router.push('/subscribe-api')
          }, 3000)
        })
    }

    if (isSubscribed.value) {
      setState('isSuccess')
      message.value = 'Congrats! You have subscribed the plan successfully.'
    }
    delay(() => {
      if (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE) {
        if (props.plan) {
          if (props.onBordRedirect) {
            utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
            router.push({ name: props.onBordRedirect })
          } else {
            useSubscribeStore().setMoreCreditsUpdated(true)
            utilModelsService.closeModal(IDEVIEWMODELS.JDROIDGETMORECREDITS)
          }
        } else {
          router.replace({ name: 'plans-payments' })
        }
      } else if (subscriptionType.value == SUBSCRIPTION_TYPE.COMPILER_API) {
        router.replace('/subscribe-api/subscribe/success')
      }
      message.value = ''
    }, 2000)
  } catch (err: any) {
    if (err.response?.status == 402) {
      setState('isError')
      message.value = err.response.data.message.split(';')[0]
    } else {
      if (
        selectedPlan.value?.value == SUBSCRIPTION_PLAN_NAME.PFREE ||
        selectedPlan.value?.name == SUBSCRIPTION_PLAN_NAME.PFREE
      ) {
        setState('isError')
        message.value =
          'Unable to process your request. Please try again, or contact JDoodle Support at hello@jdoodle.com.'
      } else {
        setState('isError')
        message.value = err.response.data.message || ''
      }
    }
  }
}

/**
 * Handle coupon input change
 */
const handleApplyCouponCode = async () => {
  message.value = ''
  verifyingCoupon.value = true

  try {
    const result = await couponValidate()

    if (result.valid) {
      const response = await getPromotionCode(coupon.value)

      if (response) {
        validCoupon.value = true

        if (subscriptionType.value == SUBSCRIPTION_TYPE.BUNDLE) {
          discountDetails.value = calcCouponDiscount(
            selectedPlan?.value?.value ?? '',
            response.percentOff
          )
        } else {
          discountDetails.value = calcCouponDiscount(
            selectedPlan?.value?.name ?? '',
            response.percentOff
          )
        }
      } else {
        invalidCoupon.value = true
        setCouponErrors('Invalid coupon code. Please ensure you’ve entered it correctly')
      }
    }
  } finally {
    verifyingCoupon.value = false
  }
}

onMounted(async () => {
  await initPage()
  if (!props.isModal) {
    getPaymentInfo()
  } else {
    await utilModelsService.onOpenModel((hs: string | null) => {
      if (
        hs === IDEVIEWMODELS.JDROIDGETMORECREDITS &&
        isUserloggedIn.value &&
        !fetchedPaymentInfo.value
      ) {
        getPaymentInfo()
      }
    })
  }
})

watch(message, (msg) => {
  if (!msg) {
    validationState.value.isLoading = false
    validationState.value.isError = false
    validationState.value.isSuccess = false
  }
})
</script>

<template>
  <div class="flex w-full flex-col items-center">
    <h3 v-show="route.params.type == 'api'" class="mb-4 text-base font-extrabold sm:text-3xl">
      Confirm Payment for Plan Upgrade
    </h3>
    <p v-show="route.params.type == 'api'" class="mb-5 text-xs sm:mb-8 sm:text-base">
      Fast and secure way to upgrade your API Plan
    </p>
    <APICard
      :title="props.apiCardTitle"
      :centerTitle="true"
      class="mb-6 w-full max-w-4xl"
      :isCustomPaymentCard="true"
      :hasBorder="true"
    >
      <div class="bg-white pt-6">
        <div class="relative mx-auto w-11/12 rounded">
          <div class="h-full">
            <div
              v-show="!tabProperties.isUnlimited && !paymentInfo"
              class="flex items-center justify-between rounded-lg border border-gray-300 px-5 py-4 shadow-sm"
            >
              <div id="apiUpdatePaymentMethod-card-element" class="w-full sm:w-3/5"></div>

              <button @click="clearCardDetails" aria-label="Delete card">
                <FontAwesomeIcon
                  icon="fa-trash"
                  class="text-gray-500 transition duration-150 hover:text-red-500"
                ></FontAwesomeIcon>
              </button>
            </div>

            <!-- Existing card info -->
            <div
              v-if="fetchedPaymentInfo && paymentInfo"
              class="flex items-center rounded-lg border border-gray-300 px-5 py-4 shadow-sm"
            >
              <div class="flex flex-shrink-0 items-center">
                <img :src="Visa" v-if="paymentInfo.cardType == 'Visa'" class="h-auto w-8" />
                <img
                  :src="Mastercard"
                  v-else-if="paymentInfo.cardType == 'MasterCard'"
                  class="h-auto w-8"
                />
                <img
                  :src="Discover"
                  v-else-if="paymentInfo.cardType == 'Discover'"
                  class="h-auto w-8"
                />
                <img
                  :src="DinersClub"
                  v-else-if="paymentInfo.cardType == 'Diners Club'"
                  class="h-auto w-8"
                />
                <img :src="JCB" v-else-if="paymentInfo.cardType == 'JCB'" class="h-auto w-8" />
                <img
                  :src="UnionPay"
                  v-else-if="paymentInfo.cardType == 'UnionPay'"
                  class="h-auto w-8"
                />
                <FontAwesomeIcon
                  v-else
                  icon="fa-regular fa-credit-card"
                  class="h-auto w-6"
                ></FontAwesomeIcon>
              </div>

              <div class="ml-4 flex w-3/4 justify-between sm:w-2/3 md:w-1/2">
                <span class="text-sm font-medium text-gray-800">
                  •••• •••• •••• {{ paymentInfo.endsWith }}
                </span>
                <span class="text-sm text-gray-500"> {{ formattedExpiresDate }} </span>
              </div>

              <div class="hs-tooltip relative ml-auto flex [--placement:top]">
                <button @click="clearPaymentInfo" aria-label="Delete card">
                  <FontAwesomeIcon
                    icon="fa-trash"
                    class="text-gray-500 transition duration-150 hover:text-red-500"
                  ></FontAwesomeIcon>
                </button>
                <span
                  class="hs-tooltip-content invisible absolute z-10 inline-block rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-sm transition-opacity hs-tooltip-shown:visible hs-tooltip-shown:opacity-100 dark:bg-slate-700"
                  role="tooltip"
                >
                  Delete Card Info
                </span>
              </div>
            </div>

            <!-- Coupon card -->
            <div v-if="!hideCoupon" class="mt-4">
              <div class="flex items-center gap-2">
                <label
                  for="with-corner-hint"
                  class="mb-2 block text-sm font-medium dark:text-white"
                >
                  Add Coupon
                </label>
                <span class="mb-2 block text-xs text-gray-400 dark:text-neutral-600">Optional</span>
              </div>
              <div class="w-full">
                <div class="flex gap-2">
                  <div class="relative w-full">
                    <input
                      type="text"
                      :value="coupon"
                      @input="handleInputChange"
                      id="with-corner-hint"
                      class="block h-full w-full rounded border border-gray-200 px-4 py-3 text-sm outline-none disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600"
                      :class="{ 'border-teal-500': validCoupon, 'border-red-500': invalidCoupon }"
                      placeholder="Enter Coupon Code"
                    />
                    <div
                      v-if="validCoupon"
                      class="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3"
                    >
                      <FontAwesomeIcon icon="fa-check" class="size-4 shrink-0 text-teal-500" />
                    </div>
                    <div
                      v-if="invalidCoupon"
                      class="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3"
                    >
                      <FontAwesomeIcon
                        icon="fa-exclamation-circle"
                        class="size-4 shrink-0 text-red-500"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    @click="handleApplyCouponCode"
                    :disabled="verifyingCoupon"
                    color="secondary"
                    variant="filled"
                    size="medium"
                    class="px-3 sm:px-6"
                  >
                    Apply
                  </Button>
                </div>
                <p v-if="couponError" class="error my-1 text-xs">
                  {{ couponError }}
                </p>
                <p v-if="validCoupon" class="success my-1 text-xs">
                  Coupon applied successfully! Your discount has been added.
                </p>
              </div>
            </div>
            <!-- Divider -->
            <hr
              v-if="!hideCoupon && validCoupon && discountDetails"
              className="my-5 h-0.5 border-t-0 surface-primary-header"
            />
            <div v-if="!hideCoupon && validCoupon && discountDetails" class="my-4 space-y-2.5">
              <p class="flex justify-between font-medium">
                <span class="text-left"> {{ selectedPlanTitle }} pricing: </span>
                <span class="text-right">
                  $ {{ discountDetails.original }} USD / {{ billingCycle.toLowerCase() }}
                </span>
              </p>
              <p class="flex justify-between font-medium">
                <span class="text-left"> Coupon discount: </span>
                <span class="text-right text-teal-500">
                  - $ {{ discountDetails.discountedAmount }} USD
                </span>
              </p>
              <p class="flex justify-between font-bold">
                <span class="text-left"> Grand Total: </span>
                <span class="text-right">
                  $ {{ discountDetails.total }} USD / {{ billingCycle.toLowerCase() }}
                </span>
              </p>
            </div>
            <!-- Divider -->
            <hr
              v-if="!hideCoupon && validCoupon && discountDetails"
              className="my-5 h-0.5 border-t-0 surface-primary-header"
            />
            <div
              :class="[props.plan ? 'h-full justify-end ' : 'h-full justify-center']"
              class="flex flex-col items-center"
            >
              <div class="my-8 flex min-h-10 w-full items-center justify-center">
                <DoodleMessage
                  v-if="message"
                  :icon="doodleMessageProps.icon"
                  :message-class="doodleMessageProps.color"
                  :message="message"
                />
              </div>
              <div class="w-fit px-5 sm:px-0 md:w-4/12 lg:w-fit" v-if="!tabProperties.isUnlimited">
                <Button
                  @click="subscribePlan"
                  :disabled="Boolean(message) || isSubscribed || verifyingCoupon"
                  color="secondary"
                  variant="filled"
                  size="medium"
                  class="mb-3 w-full"
                >
                  {{ hideCoupon ? 'Switch Plan' : ' Confirm Payment' }}
                </Button>
              </div>
              <div v-else-if="props.plan">
                <Button :data-hs-overlay="`#${IDEVIEWMODELS.JDROIDGETMORECREDITS}`"> Close </Button>
              </div>
            </div>
          </div>
          <div
            v-show="loading"
            class="absolute bottom-0 left-0 right-0 top-0 z-10 flex flex-col items-center justify-center bg-white dark:bg-background-primary-dark"
          >
            <div
              class="inline-block h-6 w-6 animate-spin rounded-full border-[3px] border-current border-t-transparent text-blue-600"
              role="status"
              aria-label="loading"
            >
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <p class="my-2 text-center text-[10px] font-light sm:text-xs">
          By subscribing, you agree to the
          <a target="_blank" href="/terms" class="text-blue-500">Terms of Service</a>
          &
          <a target="_blank" href="/privacy" class="text-blue-500">Privacy Policy</a>
        </p>
      </div>
    </APICard>
  </div>
</template>
